import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionItem,
  Button,
  Tag,
  Tile,
  TextInput,
} from '@carbon/react';
import { Api, ArrowLeft, Copy, CheckmarkFilled } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { API_GROUPS, API_ROUTES, apiHost, curlFor } from '../data/accountApiCatalog';

const ApiDocsPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const host = apiHost();
  const [copied, setCopied] = useState('');
  const [filter, setFilter] = useState('');

  const bg = isDark ? '#161616' : '#ffffff';
  const text = isDark ? '#f4f4f4' : '#161616';
  const muted = isDark ? '#c6c6c6' : '#525252';
  const border = isDark ? '#393939' : '#e0e0e0';
  const panel = isDark ? '#262626' : '#f4f4f4';
  const codeBg = isDark ? '#0b0b0b' : '#161616';

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return API_ROUTES;
    return API_ROUTES.filter((route) => {
      const hay = [
        route.path,
        route.summary,
        route.description,
        route.group,
        ...(route.metrics || []),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [filter]);

  const copyText = async (id, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(id);
      setTimeout(() => setCopied(''), 1600);
    } catch {
      /* ignore */
    }
  };

  return (
    <div style={{ background: bg, color: text, minHeight: '100vh', padding: '24px 16px 64px' }}>
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        <Button
          kind="ghost"
          size="sm"
          renderIcon={ArrowLeft}
          onClick={() => navigate('/dashboard/settings')}
          style={{ marginBottom: 12 }}
        >
          Settings
        </Button>
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '0 0 8px', fontSize: '2rem' }}>
          <Api size={32} />
          Account API
        </h1>
        <p style={{ color: muted, maxWidth: 720, lineHeight: 1.7, marginBottom: 24 }}>
          Generate a key in Settings, then call these routes with{' '}
          <code>Authorization: Bearer cv_live_…</code> or <code>X-API-Key</code>.
          Start with <strong>GET /v1</strong> to surface every route from your client, or import{' '}
          <strong>/v1/openapi.json</strong> into Postman.
        </p>

        <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', marginBottom: 28 }}>
          <Tile style={{ background: panel, border: `1px solid ${border}` }}>
            <Tag type="blue">Discover</Tag>
            <h3 style={{ margin: '8px 0 6px' }}>GET /v1</h3>
            <p style={{ color: muted, fontSize: '0.875rem', margin: 0 }}>
              JSON catalog of groups, methods, paths and parameters. No API key required.
            </p>
          </Tile>
          <Tile style={{ background: panel, border: `1px solid ${border}` }}>
            <Tag type="teal">OpenAPI</Tag>
            <h3 style={{ margin: '8px 0 6px' }}>GET /v1/openapi.json</h3>
            <p style={{ color: muted, fontSize: '0.875rem', margin: 0 }}>
              OpenAPI 3 spec for every documented route. Import into any API client.
            </p>
          </Tile>
          <Tile style={{ background: panel, border: `1px solid ${border}` }}>
            <Tag type="purple">Auth</Tag>
            <h3 style={{ margin: '8px 0 6px' }}>Account key</h3>
            <p style={{ color: muted, fontSize: '0.875rem', margin: 0 }}>
              Keys are scoped to your user. Data from other accounts is never returned.
            </p>
          </Tile>
        </div>

        <pre style={{ background: codeBg, color: '#f4f4f4', padding: 16, overflowX: 'auto', fontSize: 13, lineHeight: 1.6 }}>
{`# Surface every route
curl ${host}/v1

# OpenAPI spec
curl ${host}/v1/openapi.json

# Example: latest temperature, humidity and battery
curl -H "Authorization: Bearer cv_live_YOUR_KEY" ${host}/v1/metrics/latest`}
        </pre>
        <Button
          kind="ghost"
          size="sm"
          renderIcon={copied === 'quickstart' ? CheckmarkFilled : Copy}
          onClick={() => copyText('quickstart', `curl ${host}/v1\ncurl ${host}/v1/openapi.json\ncurl -H "Authorization: Bearer cv_live_YOUR_KEY" ${host}/v1/metrics/latest`)}
          style={{ margin: '8px 0 28px' }}
        >
          {copied === 'quickstart' ? 'Copied' : 'Copy quickstart'}
        </Button>

        <TextInput
          id="api-docs-filter"
          labelText="Filter routes"
          placeholder="temperature, gps, shipments…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ marginBottom: 16 }}
        />

        {API_GROUPS.filter((group) => filtered.some((r) => r.group === group.id)).map((group) => (
          <section key={group.id} style={{ marginBottom: 28 }}>
            <h2 style={{ fontSize: '1.25rem', margin: '0 0 4px' }}>{group.title}</h2>
            <p style={{ color: muted, margin: '0 0 12px' }}>{group.summary}</p>
            <Accordion>
              {filtered.filter((route) => route.group === group.id).map((route) => (
                <AccordionItem
                  key={route.id}
                  title={`${route.method}  ${route.path}  —  ${route.summary}`}
                >
                  <p style={{ color: muted, lineHeight: 1.7 }}>
                    {route.description || route.summary}
                  </p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0' }}>
                    <Tag type={route.auth ? 'cool-gray' : 'green'}>
                      {route.auth ? 'API key required' : 'No auth'}
                    </Tag>
                    {(route.metrics || []).map((metric) => (
                      <Tag key={metric} type="cyan">{metric}</Tag>
                    ))}
                  </div>
                  {(route.pathParams || []).length > 0 && (
                    <>
                      <h4 style={{ margin: '12px 0 6px' }}>Path parameters</h4>
                      <ul>
                        {route.pathParams.map((p) => (
                          <li key={p.name}><code>{p.name}</code> ({p.type}) — {p.description}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  {(route.query || []).length > 0 && (
                    <>
                      <h4 style={{ margin: '12px 0 6px' }}>Query parameters</h4>
                      <ul>
                        {route.query.map((p) => (
                          <li key={p.name}><code>{p.name}</code> ({p.type}) — {p.description}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  {route.example && (
                    <>
                      <h4 style={{ margin: '12px 0 6px' }}>Example response</h4>
                      <pre style={{ background: codeBg, color: '#f4f4f4', padding: 12, overflowX: 'auto', fontSize: 12 }}>
                        {JSON.stringify(route.example, null, 2)}
                      </pre>
                    </>
                  )}
                  <h4 style={{ margin: '12px 0 6px' }}>curl</h4>
                  <pre style={{ background: codeBg, color: '#f4f4f4', padding: 12, overflowX: 'auto', fontSize: 12 }}>
                    {curlFor(route, host)}
                  </pre>
                  <Button
                    kind="ghost"
                    size="sm"
                    renderIcon={copied === route.id ? CheckmarkFilled : Copy}
                    onClick={() => copyText(route.id, curlFor(route, host))}
                  >
                    {copied === route.id ? 'Copied' : 'Copy curl'}
                  </Button>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        ))}
      </div>
    </div>
  );
};

export default ApiDocsPage;
